import { useMemo } from 'react';
import { CAMPAIGN_INFO_TYPES } from '../../types/campaign.types';
import type {
  CampaignInfoType,
  CampaignInfoViewType,
  CampaignSource
} from '../../types/campaign.types';
import type { CampaignInfoDisplayStyles } from '../../components/shared/BottomCardInfo/css/base/CampaignInfoDisplayStyles';
import '../../components/shared/BottomCardInfo/css/campaign-info-display-variables.module.css';
import singleViewStyles from '../../components/shared/BottomCardInfo/css/SingleCampaignInfoDisplay.module.css';
import multiViewStyles from '../../components/shared/BottomCardInfo/css/MultiViewCampaignInfoDisplay.module.css';
import originalHasManyStyles from '../../components/shared/BottomCardInfo/css/hasManyStyles.module.css';
import { SINGLE_VIEW_MODE } from '../../../../constants';
import { useCampaignState } from '../state/useCampaignState';
import { useCampaignStatus } from '../data/campaignStatus/useCampaignStatus';
import { useCampaignDataAttributes } from '../styling/useCampaignDataAttributes';

const COLON_VARIANTS = {
  default: ':',
  empty: '',
} as const;

interface UseCampaignInfoStylesProps {
  isActiveSlot: boolean;
  campaign: CampaignSource | undefined;
  variant?: CampaignInfoViewType;
}

/**
 * Hook responsible for selecting correct CSS modules/classes and data-attributes
 * for the campaign info display based on:
 * - view mode (single vs multi)
 * - device state (portrait/mobile/collapsed)
 * - campaign timing state (active slot / finished)
 * - "has many" styling flag
 */
export const useCampaignInfoStyles = ({ isActiveSlot, campaign }: UseCampaignInfoStylesProps) => {
  const { isCollapsed, isPortrait, isTrueMobile, viewMode } = useCampaignState();
  const { isFinished } = useCampaignStatus(campaign?.id);
  const dataAttributes = useCampaignDataAttributes({ campaignId: campaign?.id });
  const hasMany = dataAttributes['data-has-many'];

  const hasManyStyles = originalHasManyStyles as unknown as CampaignInfoDisplayStyles;

  const isTabletPortrait = isPortrait && (typeof window !== 'undefined' ? window.innerWidth > 767 : false);

  /**
   * Resolve the correct CSS module based on view mode.
   */
  const campaignInfoStyles = useMemo((): CampaignInfoDisplayStyles => {
    if (viewMode === SINGLE_VIEW_MODE) {
      return singleViewStyles as unknown as CampaignInfoDisplayStyles;
    }
    return multiViewStyles as unknown as CampaignInfoDisplayStyles;
  }, [viewMode]);

  /**
   * Base colon depends on "has many" and device orientation rules.
   */
  const baseColon =
    hasMany || !isPortrait || isTabletPortrait ? COLON_VARIANTS.default : COLON_VARIANTS.empty;
  
  /**
   * Player-data types (prizes, position, score, claimed win, progress) —
   * personal stats, as opposed to static campaign timing (PERIOD) or the
   * ticking countdown (TIMER).
   */
  const isTimeType = (type: CampaignInfoType) => type === CAMPAIGN_INFO_TYPES.TIMER;
  const isPlayerDataType = (type: CampaignInfoType) => !isTimeType(type) && type !== CAMPAIGN_INFO_TYPES.PERIOD;

  /**
   * Returns correct colon variant for a given item type.
   */
  const getColonForItem = (itemType: CampaignInfoType) => {
    if (isTimeType(itemType) && isCollapsed) return COLON_VARIANTS.empty;
    if (isPlayerDataType(itemType) && isCollapsed) return COLON_VARIANTS.default;
    return baseColon;
  };

  /**
   * Collapsed attribute is applied to all types except player-data types when collapsed.
   */
  const shouldApplyCollapsedAttribute = (itemType: CampaignInfoType) =>
    isCollapsed && !isPlayerDataType(itemType);

  const getWrapperClassName = (itemType: CampaignInfoType) => {
    const baseClassName = campaignInfoStyles.campaignInfoDisplayElementsWrapper;
    const hasManyClass = hasMany ? hasManyStyles.campaignInfoDisplayElementsWrapper : '';

    const isPlayerDataCollapsed = isPlayerDataType(itemType) && isCollapsed && isTrueMobile;

    const className = isPlayerDataCollapsed
      ? `${baseClassName} ${campaignInfoStyles.totalLeftPrizesCollapsed} ${hasManyClass}`
      : `${baseClassName} ${hasManyClass}`;

    return className.trim();
  };

  /**
   * Returns data attributes for rendering/styling purposes.
   * TIMER type gets additional state attributes.
   * CLAIMED_WIN type gets a single attribute to trigger special styling.
   */
  const getDataAttributesByInfoType = (itemType: CampaignInfoType) => {
    const basicAttributes = { 'data-has-many': hasMany.toString() };

    if (itemType === CAMPAIGN_INFO_TYPES.TIMER) {
      return {
        ...basicAttributes,
        'data-active-slot': isActiveSlot.toString(),
        'data-timer': 'true',
        'data-finished': isFinished.toString(),
        'data-view': dataAttributes['data-view']
      };
    }

    if (itemType === CAMPAIGN_INFO_TYPES.CLAIMED_WIN) {
      return {
        ...basicAttributes,
        'data-claimed-win': 'true',
      };
    }

    return basicAttributes;
  };

  return {
    getCampaignInfoStyles: () => campaignInfoStyles,
    getColonForItem,
    shouldApplyCollapsedAttribute,
    getWrapperClassName,
    getDataAttributesByInfoType
  };
};